import cn from "classnames";
import React, {
	type ComponentProps,
	createContext,
	type FC,
	useContext,
	useId,
} from "react";
import {
	Form,
	type FormControlProps,
	type FormGroupProps,
} from "react-bootstrap";
import Checkbox from "../../components/Checkbox";
import { Select, type SelectProps } from "../../components/Select";
import { Icon } from "../Icon";

const ExtraFormControlIdContext = createContext<string | undefined>(undefined);

interface ExtraFormGroupProps extends Omit<FormGroupProps, "controlId"> {
	label: string;
	required?: boolean;
	id?: string;
}

const ExtraFormGroup: FC<ExtraFormGroupProps> = (props) => {
	const { className, label, required, id: _id, children, ...rest } = props;
	const internalId = useId();
	const id = _id ?? internalId;
	const classes = cn("form-group", className);

	return (
		<ExtraFormControlIdContext.Provider value={id}>
			<Form.Group className={classes} controlId={id} {...rest}>
				<Form.Label>
					{label}
					{required && <span className="form-required">*</span>}
				</Form.Label>
				{children}
			</Form.Group>
		</ExtraFormControlIdContext.Provider>
	);
};

interface ExtraFormControlProps extends FormControlProps {
	error?: string;
	touched?: boolean;
	rows?: number;
	icon?: ComponentProps<typeof Icon>["name"];
}

const ExtraFormControl: FC<ExtraFormControlProps> = (props) => {
	const { error, touched, icon, ...rest } = props;

	return (
		<div className="form-group-extra">
			<Form.Control {...rest} isInvalid={touched && error !== undefined} />
			{icon && <Icon name={icon} className="form-group-extra-icon" size={24} />}
			{touched && error && (
				<Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
			)}
		</div>
	);
};

interface ExtraFormSelectProps extends SelectProps {
	error?: string;
	touched?: boolean;
}

const ExtraFormSelect: FC<ExtraFormSelectProps> = (props) => {
	const { error, touched, id, ...rest } = props;
	const groupId = useContext(ExtraFormControlIdContext);
	return (
		<React.Fragment>
			<Select
				{...rest}
				id={id ?? groupId}
				isInvalid={touched && error !== undefined}
			/>
			{touched && error && (
				<Form.Control.Feedback type="invalid" className="d-block">
					{error}
				</Form.Control.Feedback>
			)}
		</React.Fragment>
	);
};

interface ExtraFormChecbox extends ComponentProps<typeof Checkbox> {
	error?: string;
	touched?: boolean;
}

const ExtraFormCheckbox: FC<ExtraFormChecbox> = (props) => {
	const { error, touched, ...rest } = props;
	return (
		<React.Fragment>
			<Checkbox {...rest} />
			{touched && error && (
				<Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
			)}
		</React.Fragment>
	);
};

const ExtraFormNamespace = Object.assign(
	{},
	{
		Group: ExtraFormGroup,
		Control: ExtraFormControl,
		Select: ExtraFormSelect,
		Checkbox: ExtraFormCheckbox,
	},
);

export { ExtraFormNamespace as ExtraForm };
